import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { isEqual } from 'lodash-es';
import moment from 'moment-timezone';

import { DashpivotEvent, EventNotifierService, EventTypes } from '@site-mate/dashpivot-shared-library';
import { SubscriptionStatuses } from '@site-mate/sitemate-global-shared';

import { WorkspaceSwitcherService } from 'app/org/workspace-switcher/workspace-switcher.service';
import { SegmentService } from 'app/segment/segment.service';
import { UserService } from 'app/user/user.service';

import { AccountUpgradeModalComponent } from '../account-upgrade-modal/account-upgrade-modal.component';

@Component({
  selector: 'cc-account-upgrade-widget',
  templateUrl: './account-upgrade-widget.component.html',
  styleUrls: ['./account-upgrade-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountUpgradeWidgetComponent implements OnChanges {
  readonly sevenDays = 7 * 24 * 60 * 60 * 1000;
  @Input() companyId: string;
  @Input() smcDemo: boolean;
  @Input() trialExpiration: Date;
  @Input() companySubscriptionStatus: SubscriptionStatuses;

  trialDaysLeft: string;
  trialExpirationPeriod: number;
  expirationInMilliSeconds: number;
  popoverMessage: string;
  readonly upgradeAccountEvent = 'Upgrade account';

  constructor(
    private readonly modal: NgbModal,
    private readonly userService: UserService,
    private readonly segmentService: SegmentService,
    private readonly workspaceSwitcherService: WorkspaceSwitcherService,
  ) {}

  private calculateTrialExpirationPeriod() {
    this.expirationInMilliSeconds = new Date(this.trialExpiration).getTime();
    moment.relativeTimeThreshold('d', 31);
    this.trialDaysLeft = moment(this.expirationInMilliSeconds).fromNow();
    this.trialExpirationPeriod = this.expirationInMilliSeconds - new Date().getTime();

    const expirationDateInIso = moment(this.expirationInMilliSeconds).format('MMM DD, YYYY');
    this.popoverMessage = `Your free trial ends in ${this.trialDaysLeft} on ${expirationDateInIso}`;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.hasChanges(changes)) {
      this.calculateTrialExpirationPeriod();
      void this.showUpgradeModalForExpiredAccounts();
      void EventNotifierService.notify(
        new DashpivotEvent(EventTypes.NavLinkClicked, { Context: this.upgradeAccountEvent }),
        this.segmentService,
      );
    }
  }

  async showAccountUpgrade() {
    if (!this.modal.hasOpenModals()) {
      const modalRef = this.modal.open(AccountUpgradeModalComponent, {
        windowClass: 'modal-500',
        modalDialogClass: 'modal-dialog-top',
        beforeDismiss: () => !this.shouldForceModal(),
      });
      modalRef.componentInstance.expiration = this.expirationInMilliSeconds;
      modalRef.componentInstance.isPausedOrChurned = [
        SubscriptionStatuses.Paused,
        SubscriptionStatuses.Churned,
      ].includes(this.companySubscriptionStatus);
      try {
        await modalRef.result;
        await this.openWorkspaceSwitcher();
      } catch {
        await this.showUpgradeModalForExpiredAccounts();
      }
    }
  }

  async showUpgradeModalForExpiredAccounts() {
    if (this.shouldForceModal()) {
      await this.showAccountUpgrade();
    }
  }

  private shouldForceModal() {
    if (this.userService.isAdmin()) {
      return false;
    }

    if (this.smcDemo) {
      return false;
    }

    const hasTrialEnded = this.trialExpirationPeriod < 0;
    const isFreeOrExpiredTrial = [SubscriptionStatuses.FreeTrial, SubscriptionStatuses.ExpiredTrial].includes(
      this.companySubscriptionStatus,
    );
    const isPausedOrChurned = [SubscriptionStatuses.Paused, SubscriptionStatuses.Churned].includes(
      this.companySubscriptionStatus,
    );

    const shouldShowModal = isPausedOrChurned || (isFreeOrExpiredTrial && hasTrialEnded);
    return shouldShowModal;
  }

  async openWorkspaceSwitcher() {
    const { event, context } = await this.workspaceSwitcherService.openWorkspaceSwitcher();
    if (event === 'dismissed' || context === this.companyId) {
      await this.showUpgradeModalForExpiredAccounts();
    }
  }

  private hasChanges(changes: SimpleChanges) {
    return Object.keys(changes).some(
      (key) => !isEqual(changes[key].previousValue, changes[key].currentValue),
    );
  }
}
